import React, { useRef, useState, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';

import type { IProperty } from 'src/services/api';
import { usePropertyForm } from 'src/components/create-or-update-property-modal/use-property-form';
import PropertyForm from 'src/components/create-or-update-property-modal/PropertyForm';
import PropertyImageEditor from 'src/components/create-or-update-property-modal/PropertyImageEditor';
import type { PropertyFactsSectionRef } from 'src/components/create-or-update-property-modal/PropertyFactsSection';

import styles from 'src/components/create-or-update-property-modal/CreateOrUpdatePropertyModal.module.css';

type Props = {
    title?: string;
    property?: IProperty;
    defaultValues?: Partial<IProperty>;
    opened: boolean;
    onClose: () => void;
    onSave?: (property: IProperty) => Promise<void> | void;
};

export default function CreateOrUpdatePropertyModal({
    title: propsTitle,
    property,
    defaultValues,
    opened,
    onClose,
    onSave,
}: Props) {

    const [imageFile, setImageFile] = useState<File | null>(null);

    const onSuccess = (_property: IProperty) => {
        setImageFile(null);
        onClose();
        onSave?.(_property);
    };

    const { form, submit } = usePropertyForm({
        onSuccess,
        property,
        defaultValues,
        imageFile,
    });
    const factsRef = useRef<PropertyFactsSectionRef>(null);
    const isEditing = !!property;
    const title = propsTitle || (isEditing ? 'Edit Property' : 'Create Property');

    const handleSave = useCallback(
        (event?: React.BaseSyntheticEvent) =>
            form.handleSubmit(async (values) => {
                const factsSaved = await (factsRef.current?.applyChanges() ?? Promise.resolve(true));
                if (!factsSaved) {
                    return;
                }

                await submit(values);
            })(event),
        [form, submit]
    );

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Dialog
                open={opened}
                onClose={onClose}
                PaperProps={{
                    sx: {
                        width: '100%',
                        maxWidth: 'md',
                        minHeight: '80%',
                    }
                }}
            >
                <DialogTitle>
                    {title}
                </DialogTitle>
                <DialogContent className={styles.content}>
                    <div className={styles.header}>
                        <PropertyImageEditor
                            form={form}
                            currentImageUrl={property?.image_url}
                            selectedFile={imageFile}
                            onSelectFile={setImageFile}
                        />
                    </div>
                    <PropertyForm
                        form={form}
                        onSubmit={handleSave}
                        currentProperty={property}
                        factsRef={factsRef}
                    />
                </DialogContent>

                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        data-testid="cancel-property-button"
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        variant="contained"
                        loading={form.formState.isSubmitting}
                        onClick={handleSave}
                        data-testid="save-property-button"
                    >
                        Save
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
}
